import React, { memo, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Handle, Position } from '@xyflow/react'
import { useFormattingContext } from '../../../../FormattingProvider/FormattingContext'
import { getVariantStyles } from './variants'

const HANDLE_STYLE = { width: 5, height: 5, opacity: 0 }

const DIAMOND_DIMENSIONS = {
  lg: 120,
  md: 90,
  sm: 64
}

const DIAMOND_FONT_SIZES = {
  lg: '0.75rem',
  md: '0.625rem',
  sm: '0.5625rem'
}

function FlowGraphDiamond ({ data, selected }) {
  const {
    nameValue, nameConfig,
    descriptionValue, descriptionConfig,
    variant = 'accent',
    size = 'md',
    color
  } = data

  const { formatter } = useFormattingContext()
  const variantStyles = getVariantStyles(variant, color)
  const dim = DIAMOND_DIMENSIONS[size] || DIAMOND_DIMENSIONS.md

  const formattedName = nameConfig?.format
    ? formatter(nameValue, nameConfig.format)
    : nameValue

  const formattedDesc = descriptionConfig?.format && descriptionValue != null
    ? formatter(descriptionValue, descriptionConfig.format)
    : descriptionValue

  const outerStyle = useMemo(() => ({
    width: dim,
    height: dim,
    position: 'relative',
    cursor: 'pointer'
  }), [dim])

  const diamondStyle = useMemo(() => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: variantStyles.background,
    border: `2px solid ${selected ? variantStyles.selectedBorderColor : variantStyles.borderColor}`,
    borderRadius: 6,
    transform: 'rotate(45deg)',
    boxShadow: selected ? '0 0 0 3px rgba(0,0,0,0.08)' : '0 2px 6px rgba(0,0,0,0.12)',
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
    boxSizing: 'border-box'
  }), [variantStyles, selected])

  const contentStyle = useMemo(() => ({
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    padding: 8,
    boxSizing: 'border-box',
    overflow: 'hidden'
  }), [])

  const nameStyle = {
    color: variantStyles.color,
    fontWeight: 600,
    fontSize: DIAMOND_FONT_SIZES[size] || DIAMOND_FONT_SIZES.md,
    lineHeight: 1.2,
    textAlign: 'center',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    maxWidth: dim * 0.7,
    ...(nameConfig?.style || {})
  }

  const descStyle = {
    color: variantStyles.color,
    opacity: 0.8,
    fontSize: '0.5625em',
    textAlign: 'center',
    marginTop: 1,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    maxWidth: dim * 0.7,
    ...(descriptionConfig?.style || {})
  }

  return (
    <div style={outerStyle}>
      <div style={diamondStyle} />
      <div style={contentStyle}>
        <Handle type='target' position={Position.Top} style={{ ...HANDLE_STYLE, background: variantStyles.borderColor }} />
        {formattedName != null && (
          <div className={nameConfig?.className || ''} style={nameStyle}>
            {formattedName}
          </div>
        )}
        {formattedDesc != null && (
          <div className={descriptionConfig?.className || ''} style={descStyle}>
            {formattedDesc}
          </div>
        )}
        <Handle type='source' position={Position.Bottom} style={{ ...HANDLE_STYLE, background: variantStyles.borderColor }} />
      </div>
    </div>
  )
}

FlowGraphDiamond.propTypes = {
  data: PropTypes.shape({
    nameValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    nameConfig: PropTypes.object,
    descriptionValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    descriptionConfig: PropTypes.object,
    variant: PropTypes.string,
    color: PropTypes.object,
    size: PropTypes.string
  }),
  selected: PropTypes.bool
}

export default memo(FlowGraphDiamond)
